import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import map from 'lodash/map';
import orderBy from 'lodash/orderBy';
import {
	canAddHighscoreEntries,
	getNotSyncedHighscoreEntries,
	mergeHighscore
} from '../data/Highscore';
import type { HighscoreEntry } from '../data/Highscore';
import { EMPTY_TOTAL_SCORE, sumPoints } from '../data/Score';
import type { TotalScore } from '../data/Score';
import type { RootState } from './Store';
import { getHighscoreApi } from '../utils/clientapi';
import type {
	EntityModelHighscoreEntry,
	GetCollectionResourceHighscoreentryGet1Request,
	PostCollectionResourceHighscoreentryPostRequest
} from 'mml-client-api';

export interface HighscoreSliceState {
	entries: HighscoreEntry[];
}

export const getSortedEntries = (entries: HighscoreEntry[]): HighscoreEntry[] =>
	orderBy(
		entries,
		[(entry) => sumPoints(entry.score), (entry) => entry.playerName],
		['desc', 'asc']
	);

const initialState: HighscoreSliceState = {
	entries: []
};

export interface LoadHighscoreResult {
	entries: HighscoreEntry[];
}

export type LoadHighscoreArgs = void;

const fromEntityModelHighscore = (
	entry: EntityModelHighscoreEntry
): HighscoreEntry => {
	return {
		playerName: entry.playerName ?? '',
		score: entry.score ?? EMPTY_TOTAL_SCORE,
		synced: true
	};
};

export const loadHighscore = createAsyncThunk<
	LoadHighscoreResult,
	LoadHighscoreArgs,
	{ state: RootState }
>(
	'highscore/loadHighscore',
	async (_: LoadHighscoreArgs, thunkApi): Promise<LoadHighscoreResult> => {
		const { configuration } = thunkApi.getState();

		if (!configuration.globalHighscoreEnabled) {
			return Promise.reject();
		}

		const api = getHighscoreApi(configuration);
		const requestParameters: GetCollectionResourceHighscoreentryGet1Request = {
			size: configuration.maxHighscoreEntryCount,
			sort: ['score.sum,desc']
		};
		const response = await api.getCollectionResourceHighscoreentryGet1(
			requestParameters
		);

		const entries = map(
			response.embedded?.highscoreEntries,
			fromEntityModelHighscore
		);

		return {
			entries
		};
	}
);

export interface AddHighscoreEntryArgs {
	playerName: string;
	score: TotalScore;
}

const fromAddHighscoreEntryArgs = (
	entry: AddHighscoreEntryArgs
): HighscoreEntry => {
	return {
		playerName: entry.playerName,
		score: entry.score,
		synced: false
	};
};

export type AddHighscoreEntryResult = HighscoreEntry;

export const addHighscoreEntry = createAsyncThunk<
	AddHighscoreEntryResult,
	AddHighscoreEntryArgs,
	{ state: RootState }
>(
	'highscore/addHighscoreEntry',
	async (
		args: AddHighscoreEntryArgs,
		thunkApi
	): Promise<AddHighscoreEntryResult> => {
		const { configuration } = thunkApi.getState();
		const entry = args;

		if (configuration.globalHighscoreEnabled) {
			const api = getHighscoreApi(configuration);
			const requestParameters: PostCollectionResourceHighscoreentryPostRequest =
				{
					highscoreEntryRequestBody: entry
				};

			const response = await api.postCollectionResourceHighscoreentryPost(
				requestParameters
			);

			return Promise.resolve(fromEntityModelHighscore(response));
		}

		return Promise.reject();
	}
);

const highscoreSlice = createSlice({
	name: 'highscore',
	initialState,
	reducers: {
		clearHighscore: (state): void => {
			state.entries = [];
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(loadHighscore.fulfilled, (state, action) => {
				const { entries } = action.payload;

				const notSyncedEntries = getNotSyncedHighscoreEntries(state.entries);
				state.entries = mergeHighscore(notSyncedEntries, entries);
			})
			.addCase(addHighscoreEntry.pending, (state, action) => {
				const entry = action.meta.arg;

				if (
					!canAddHighscoreEntries(state.entries, entry.playerName, entry.score)
				) {
					throw new Error('Entry already in highscore');
				}

				state.entries = mergeHighscore(state.entries, [
					fromAddHighscoreEntryArgs(entry)
				]);
			})
			.addCase(addHighscoreEntry.fulfilled, (state, action) => {
				const entry = action.payload;

				state.entries = mergeHighscore(state.entries, [entry]);
			});
	}
});

export const { clearHighscore } = highscoreSlice.actions;
export default highscoreSlice.reducer;
